// src/features/deadlines/components/MorningDeadlineModal.tsx
// =============================================================================
// Tagesstart-Modal: Zeigt nach dem Login die heute faelligen und ueberfaelligen
// Fristen des angemeldeten Mitarbeiters und zwingt zur bewussten Bestaetigung
// jeder einzelnen Frist (Sammelbestaetigung am Ende erst aktiv, wenn alle
// gehakt sind).
//
// - Laedt seine Kunden (zustaendiger_mitarbeiter_id = current staff)
// - Berechnet Fristen mit den existierenden TS-Utils
// - Filtert auf "ueberfaellig oder heute faellig", respektiert Snooze
// - Ruhende Fristen (Wartet auf Kunden) erscheinen nicht, sie verlangen keine Handlung
// - Wegklicken wird in localStorage pro Tag/User gespeichert (kein DB-Schema noetig)
// =============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../shared/services/supabase';
import { Customer, CustomerMonthlyStatus, CustomerIndividualDeadline } from '../../../shared/types';
import {
  calculateEffectiveLohnlaufDate,
  calculateLohnscheinVersandForLauf,
  calculateLohnsteuerDeadline,
  getCurrentWorkingMonth,
  formatDate,
  type LohnscheinVersandLaufConfig
} from '../../../shared/utils/dateUtils';
import { customerIndividualDeadlineService } from '../../../services/customer/CustomerIndividualDeadlineService';
import { ermittleRuhendeFristen, lohnscheinRelativAus } from '../../../shared/utils/deadlineUtils';

interface MorningDeadlineModalProps {
  userEmail: string;
}

interface DueDeadline {
  customerId: string;
  customerName: string;
  title: string;
  date: Date;
  daysOverdue: number; // 0 = heute, >0 = ueberfaellig
  uniqueKey: string;
}

const STORAGE_KEY_PREFIX = 'xtax_morning_modal_dismissed_';

function getTodayDateString(): string {
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getTodayStorageKey(email: string): string {
  return `${STORAGE_KEY_PREFIX}${email}_${getTodayDateString()}`;
}

function cleanupOldDismissals(): void {
  // Entferne abgelaufene Eintraege (alles, was nicht heute ist)
  try {
    const todayDate = getTodayDateString();
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX) && !key.endsWith(`_${todayDate}`)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));
  } catch {
    /* localStorage nicht verfuegbar - egal */
  }
}

export const MorningDeadlineModal: React.FC<MorningDeadlineModalProps> = ({ userEmail }) => {
  const [loading, setLoading] = useState(true);
  const [dueDeadlines, setDueDeadlines] = useState<DueDeadline[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<Set<string>>(new Set());
  const [closed, setClosed] = useState(false);

  // Heute schon dismissed? Dann gar nicht laden.
  const alreadyDismissedToday = useMemo(() => {
    if (!userEmail) return false;
    try {
      return localStorage.getItem(getTodayStorageKey(userEmail)) === '1';
    } catch {
      return false;
    }
  }, [userEmail]);

  useEffect(() => {
    cleanupOldDismissals();
  }, []);

  useEffect(() => {
    if (!userEmail || alreadyDismissedToday) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadData = async () => {
      try {
        // 1. Finde aktuellen Staff anhand der Email
        const { data: staffData, error: staffError } = await supabase
          .from('xtax_staff')
          .select('id')
          .eq('email', userEmail)
          .eq('is_active', true)
          .maybeSingle();

        if (staffError || !staffData) {
          if (!cancelled) {
            setDueDeadlines([]);
            setLoading(false);
          }
          return;
        }

        const staffId = staffData.id;

        // 2. Lade Kunden des Staffs
        const { data: customers, error: custError } = await supabase
          .from('customers')
          .select('*')
          .eq('zustaendiger_mitarbeiter_id', staffId)
          .eq('deleted', false)
          .eq('ist_testmandant', false) // Testmandanten erscheinen nicht im Fristen-Fenster
          .limit(5000);

        if (custError || !customers || customers.length === 0) {
          if (!cancelled) {
            setDueDeadlines([]);
            setLoading(false);
          }
          return;
        }

        const customerIds = customers.map((c: Customer) => c.id);

        // 3. Lade Status, monthly_deadlines, individuelle Deadlines sowie die
        //    Lohnschein-Konfiguration und deren Fertigmeldungen parallel
        const [statusRes, mdRes, indRes, lsConfigRes, lsLaufStatusRes] = await Promise.all([
          supabase
            .from('customer_monthly_status')
            .select('*')
            .in('customer_id', customerIds)
            .limit(5000),
          supabase
            .from('monthly_deadlines')
            .select('year, month, lohnsteuer_frist, beitragsnachweis_frist'),
          supabase
            .from('customer_individual_deadlines')
            .select('*')
            .in('customer_id', customerIds)
            .eq('is_active', true)
            .limit(5000),
          supabase
            .from('customer_lohnschein_versand_configs')
            .select('customer_id, lohnlauf, tag, verschiebung, monat_offset, bezug_werktage_offset')
            .in('customer_id', customerIds)
            .eq('is_active', true)
            .limit(100000),
          supabase
            .from('lohnschein_versand_lauf_status')
            .select('customer_id, arbeitsjahr, arbeitsmonat, lohnlauf, completed')
            .in('customer_id', customerIds)
            .limit(100000)
        ]);

        const allStatuses: CustomerMonthlyStatus[] = (statusRes.data || []) as CustomerMonthlyStatus[];
        const monthlyDeadlinesData = mdRes.data || [];
        const individualDeadlinesData: CustomerIndividualDeadline[] = (indRes.data || []) as CustomerIndividualDeadline[];

        const indByCustomer: { [cid: string]: CustomerIndividualDeadline[] } = {};
        individualDeadlinesData.forEach(d => {
          if (!indByCustomer[d.customer_id]) indByCustomer[d.customer_id] = [];
          indByCustomer[d.customer_id].push(d);
        });

        // Zwei-Lauf-Modell: aktive Lohnschein-Konfigurationen pro Kunde/Lohnlauf.
        // Gleiche Quelle wie das Fristenboard, damit beide Ansichten dieselben
        // Fristen kennen.
        const lohnscheinConfigMap = new Map<string, LohnscheinVersandLaufConfig[]>();
        (lsConfigRes.data || []).forEach((c: { customer_id: string; lohnlauf: number; tag: number | null; verschiebung: string | null; monat_offset: number | null; bezug_werktage_offset: number | null }) => {
          const list = lohnscheinConfigMap.get(c.customer_id) || [];
          list.push({
            lohnlauf: c.lohnlauf as 1 | 2,
            tag: c.tag,
            verschiebung: c.verschiebung,
            monat_offset: c.monat_offset,
            bezug_werktage_offset: c.bezug_werktage_offset
          });
          lohnscheinConfigMap.set(c.customer_id, list);
        });

        // Zwei-Lauf-Modell: Fertigmeldungen pro Kunde/Arbeitsmonat/Lohnlauf.
        // Die Erledigung liegt ausschließlich hier, nicht im Monatsstatus.
        const lohnscheinLaufStatusMap = new Map<string, boolean>();
        (lsLaufStatusRes.data || []).forEach((s: { customer_id: string; arbeitsjahr: number; arbeitsmonat: number; lohnlauf: number; completed: boolean }) => {
          lohnscheinLaufStatusMap.set(`${s.customer_id}_${s.arbeitsjahr}_${s.arbeitsmonat}_${s.lohnlauf}`, !!s.completed);
        });

        // 4. Pro Kunde: berechne faellige Fristen
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const result: DueDeadline[] = [];

        for (const customer of customers as Customer[]) {
          const workingMonth = getCurrentWorkingMonth(customer, allStatuses);
          const status = allStatuses.find(
            s =>
              s.customer_id === customer.id &&
              s.year === workingMonth.year &&
              s.month === workingMonth.month
          ) || null;

          type Candidate = { title: string; date?: Date; completed: boolean; fieldName: string };
          const candidates: Candidate[] = [];

          // Lohnlauf 1 + 2 via SSoT (calculateEffectiveLohnlaufDate) — der Status
          // liegt hier bereits vor und liefert eine ggf. markierte Datenanlieferung,
          // sodass das Morgen-Modal dasselbe Fristdatum zeigt wie das Fristenboard.
          if (customer.erster_lohnlauf_tag) {
            const date = calculateEffectiveLohnlaufDate(
              customer,
              1,
              workingMonth.year,
              workingMonth.month,
              status
            );
            candidates.push({
              title: customer.zweiter_lohnlauf_tag ? 'Erster Lohnlauf' : 'Abrechnung finalisieren',
              date,
              completed: !!status?.lohnlauf_1_completed,
              fieldName: 'lohnlauf_1_completed'
            });
          }

          if (customer.zweiter_lohnlauf_tag) {
            const date = calculateEffectiveLohnlaufDate(
              customer,
              2,
              workingMonth.year,
              workingMonth.month,
              status
            );
            candidates.push({
              title: 'Zweiter Lohnlauf und Abrechnung finalisieren',
              date,
              completed: !!status?.lohnlauf_2_completed,
              fieldName: 'lohnlauf_2_completed'
            });
          }

          const lohnsteuerDate = calculateLohnsteuerDeadline(
            workingMonth.year,
            workingMonth.month,
            customer.lohnsteuer_zeitraum,
            monthlyDeadlinesData
          );
          if (lohnsteuerDate) {
            candidates.push({
              title: 'Lohnsteuer',
              date: lohnsteuerDate,
              completed: !!status?.lohnsteuer_completed,
              fieldName: 'lohnsteuer_completed'
            });
          }

          if (customer.sv_pflicht) {
            const md = monthlyDeadlinesData.find(
              (d: any) => d.year === workingMonth.year && d.month === workingMonth.month
            );
            if (md && md.beitragsnachweis_frist) {
              candidates.push({
                title: 'SV-Beiträge',
                date: new Date(md.beitragsnachweis_frist),
                completed: !!status?.sv_beitraege_completed,
                fieldName: 'sv_beitraege_completed'
              });
            }
          }

          // Lohnschein-Versand an Arbeitnehmer (Zwei-Lauf-Modell): eine Frist je
          // konfiguriertem Lohnlauf. Fälligkeit und Inhaltsmonat kommen aus
          // calculateLohnscheinVersandForLauf, die Erledigung aus
          // lohnschein_versand_lauf_status. Identischer Pfad wie im Fristenboard,
          // inklusive der Feldnamen, damit ein dort gesetzter Snooze hier greift.
          const lsConfigs = lohnscheinConfigMap.get(customer.id) || [];
          for (const cfg of lsConfigs) {
            const res = calculateLohnscheinVersandForLauf(
              customer,
              cfg,
              workingMonth.year,
              workingMonth.month,
              status
            );
            const erledigt = lohnscheinLaufStatusMap.get(
              `${customer.id}_${workingMonth.year}_${workingMonth.month}_${cfg.lohnlauf}`
            ) || false;
            candidates.push({
              title: `Lohnschein-Versand ${cfg.lohnlauf}. Lohnlauf (für ${String(res.periode.month).padStart(2, '0')}/${res.periode.year})`,
              date: res.faelligkeit,
              completed: erledigt,
              fieldName: cfg.lohnlauf === 1 ? 'lohnschein_versand_1_completed' : 'lohnschein_versand_2_completed'
            });
          }

          (indByCustomer[customer.id] || []).forEach(ind => {
            const date = customerIndividualDeadlineService.calculateIndividualDeadlineDate(
              workingMonth.year,
              workingMonth.month,
              ind,
              customer,
              status
            );
            if (!date) return;
            const isCompleted = !ind.is_recurring
              ? !!ind.completed
              : !!status?.individual_deadlines_status?.[ind.id];
            candidates.push({
              title: ind.title,
              date,
              completed: isCompleted,
              fieldName: `individual_deadline_${ind.id}`
            });
          });

          // WARTET AUF KUNDEN: Fristen, die gerade beim Kunden ruhen, verlangen
          // keine Handlung und erscheinen deshalb nicht im Tagesstart-Modal.
          // Gleiche Regel wie im Fristenboard (ermittleRuhendeFristen).
          const ruhendeFristen = ermittleRuhendeFristen(
            customer,
            status,
            indByCustomer[customer.id] || [],
            lohnscheinRelativAus(lsConfigs)
          );

          // Filter mit Snooze-Logik
          for (const c of candidates) {
            if (c.completed || !c.date) continue;
            if (ruhendeFristen.has(c.fieldName)) continue;

            let effectiveDate = new Date(c.date.getFullYear(), c.date.getMonth(), c.date.getDate());

            const snoozeInfo = status?.snoozed_deadlines?.[c.fieldName];
            if (snoozeInfo) {
              const snoozedUntil = new Date(snoozeInfo.snoozed_until + 'T00:00:00');
              if (snoozedUntil > today) continue; // aktiv gesnoozed
              if (snoozedUntil > effectiveDate) effectiveDate = snoozedUntil;
            }

            if (effectiveDate <= today) {
              const customerName =
                customer.company ||
                [customer.vorname, customer.nachname].filter(Boolean).join(' ') ||
                'Unbekannter Kunde';
              const daysOverdue = Math.round(
                (today.getTime() - effectiveDate.getTime()) / (1000 * 60 * 60 * 24)
              );
              result.push({
                customerId: customer.id,
                customerName,
                title: c.title,
                date: effectiveDate,
                daysOverdue,
                uniqueKey: `${customer.id}_${c.fieldName}`
              });
            }
          }
        }

        // Sortierung: am laengsten ueberfaellig zuerst
        result.sort((a, b) => b.daysOverdue - a.daysOverdue);

        if (!cancelled) {
          setDueDeadlines(result);
          setLoading(false);
        }
      } catch (err) {
        console.error('[MorningDeadlineModal] Error loading data:', err);
        if (!cancelled) {
          setDueDeadlines([]);
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [userEmail, alreadyDismissedToday]);

  const allChecked = dueDeadlines.length > 0 && checkedKeys.size === dueDeadlines.length;

  const handleToggle = (key: string) => {
    setCheckedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    try {
      localStorage.setItem(getTodayStorageKey(userEmail), '1');
    } catch {
      /* egal */
    }
    setClosed(true);
  };

  if (loading) return null;
  if (closed) return null;
  if (alreadyDismissedToday) return null;
  if (dueDeadlines.length === 0) return null;

  // Gruppiere nach Kunde fuer Anzeige
  const grouped: { [cid: string]: { customerName: string; items: DueDeadline[] } } = {};
  dueDeadlines.forEach(d => {
    if (!grouped[d.customerId]) {
      grouped[d.customerId] = { customerName: d.customerName, items: [] };
    }
    grouped[d.customerId].items.push(d);
  });

  const totalOverdue = dueDeadlines.filter(d => d.daysOverdue > 0).length;
  const totalToday = dueDeadlines.filter(d => d.daysOverdue === 0).length;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        fontFamily: 'inherit'
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          maxWidth: '720px',
          width: '92%',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 28px 16px',
            borderBottom: '1px solid #E5E7EB'
          }}
        >
          <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>
            Guten Morgen – diese Fristen brauchen heute Deine Aufmerksamkeit
          </h2>
          <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#6B7280' }}>
            <strong style={{ color: '#DC2626' }}>{totalOverdue} überfällig</strong>
            {' '}·{' '}
            <strong style={{ color: '#D97706' }}>{totalToday} heute fällig</strong>
            {' '}· Bitte hake jede Frist einzeln ab, bevor Du fortfährst.
          </p>
        </div>

        {/* Body (scrollbar) */}
        <div style={{ padding: '12px 28px', overflowY: 'auto', flex: 1 }}>
          {Object.values(grouped).map(group => (
            <div key={group.customerName} style={{ marginBottom: '16px' }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '15px',
                  color: '#1F2937',
                  margin: '12px 0 6px'
                }}
              >
                {group.customerName}
              </div>
              {group.items.map(item => {
                const isOverdue = item.daysOverdue > 0;
                const checked = checkedKeys.has(item.uniqueKey);
                return (
                  <label
                    key={item.uniqueKey}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 12px',
                      marginBottom: '6px',
                      borderRadius: '6px',
                      border: '1px solid #E5E7EB',
                      backgroundColor: checked
                        ? '#F0FDF4'
                        : isOverdue
                        ? '#FEF2F2'
                        : '#FFFBEB',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggle(item.uniqueKey)}
                      style={{
                        width: '18px',
                        height: '18px',
                        marginRight: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{ flex: 1, fontSize: '14px', color: '#1F2937' }}>
                      {item.title}
                    </div>
                    <div style={{ fontSize: '13px', textAlign: 'right', minWidth: '180px' }}>
                      <div style={{ color: '#6B7280' }}>{formatDate(item.date)}</div>
                      <div
                        style={{
                          color: isOverdue ? '#DC2626' : '#D97706',
                          fontWeight: 600
                        }}
                      >
                        {isOverdue
                          ? `${item.daysOverdue} Tag${item.daysOverdue === 1 ? '' : 'e'} überfällig`
                          : 'heute fällig'}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 28px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ fontSize: '13px', color: '#6B7280' }}>
            {checkedKeys.size} / {dueDeadlines.length} bestätigt
          </div>
          <button
            onClick={handleConfirm}
            disabled={!allChecked}
            style={{
              padding: '10px 22px',
              backgroundColor: allChecked ? '#059669' : '#D1D5DB',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: allChecked ? 'pointer' : 'not-allowed',
              transition: 'background-color 0.15s'
            }}
          >
            Verstanden – ich kümmere mich heute darum
          </button>
        </div>
      </div>
    </div>
  );
};
